import React, { useState, useRef } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Checkbox
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSnackbar } from 'notistack';
import { Transition } from 'Shared/Transition';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import { AlertDialog } from 'Shared/AlertDialog';
import http from 'Utils/http';
import config from '../../../../config';
import { Controller, useForm } from 'react-hook-form';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs/components/prism-core';
import { copyText } from 'Utils/clipboard';

const normalizeIssues = (issues) => {
    if (!issues) {
        return [];
    }

    if (Array.isArray(issues)) {
        return issues;
    }

    if (typeof issues === 'object') {
        return Object.values(issues);
    }

    return [];
};

const DEFAULT_ISSUE_MESSAGE = 'Need to fix errors in JSON';

const buildIssueMessage = (issues = []) =>
    issues
        .map(({ message, pointer }) => (pointer ? `${message} (${pointer})` : message))
        .filter(Boolean)
        .join('; ');

const formatImportError = (payload, fallback = 'Unknown error') => {
    if (!payload) {
        return fallback;
    }

    const normalizedIssues = normalizeIssues(payload.issues);
    if (normalizedIssues.length > 0) {
        return payload.message || DEFAULT_ISSUE_MESSAGE;
    }

    const issueMessage = buildIssueMessage(normalizedIssues);
    return issueMessage || payload.error || payload.message || fallback;
};

const FlowImportIntoExisting = ({ flowId, visible, setVisible, onImportSuccess, onImportAndRun }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [code, setCode] = useState('');
    const [importIssues, setImportIssues] = useState([]);
    const [dryRun, setDryRun] = useState(false);
    const [aiPrompt, setAiPrompt] = useState('');
    const [showWarningBeforeImport, setShowWarningBeforeImport] = useState(false);
    const [editorFocused, setEditorFocused] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const hiddenTextareaRef = useRef(null);
    const runAfterImportRef = useRef(false);

    const {
        control,
        setValue,
        getValues,
        formState: { errors }
    } = useForm({
        defaultValues: {
            code: ''
        }
    });

    const onClose = () => {
        setCode('');
        setImportIssues([]);
        setDryRun(false);
        setAiPrompt('');
        setShowWarningBeforeImport(false);
        setVisible(false);
    };

    const writePromptToHiddenTextarea = (prompt) => {
        const textArea = hiddenTextareaRef.current;
        if (textArea && prompt) {
            textArea.value = prompt;
        }
    };

    const copyToClipboard = () => {
        const textArea = hiddenTextareaRef.current;
        if (!textArea || !textArea.value) {
            enqueueSnackbar('Nothing to copy', { action: CustomSnackbar, variant: 'error' });
            return;
        }

        copyText(textArea.value)
            .then(() => {
                enqueueSnackbar('Prompt copied! Paste it into ChatGPT or Claude.', {
                    action: CustomSnackbar,
                    variant: 'success'
                });
            })
            .catch(() => {
                enqueueSnackbar('Failed to copy', { action: CustomSnackbar, variant: 'error' });
            });
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.type !== 'application/json') {
                enqueueSnackbar('Please upload the file in JSON format.', {
                    action: CustomSnackbar,
                    variant: 'error',
                });
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const parsed = JSON.parse(e.target.result);
                    setCode(JSON.stringify(parsed, null, 4));
                    setValue('code', JSON.stringify(parsed, null, 4));
                } catch (err) {
                    enqueueSnackbar('Error parsing JSON file.', {
                        action: CustomSnackbar,
                        variant: 'error',
                    });
                }
            };
            reader.readAsText(file);
        }
    };


    const performImport = () => {
        const codeValue = getValues('code');

        setIsImporting(true);
        http.post(`/flow/${flowId}/import`, codeValue, {
            params: { dry_run: dryRun }
        })
            .then(({ data }) => {
                if (data.status !== 'success') {
                    const issues = normalizeIssues(data.issues);
                    if (issues.length === 0 && (data.error || data.message)) {
                        issues.push({
                            message: data.error || data.message,
                            pointer: data.trace?.[0]?.file ? `${data.trace[0].file}:${data.trace[0].line}` : null
                        });
                    }
                    setImportIssues(issues);
                    setAiPrompt(data.ai_prompt || '');
                    writePromptToHiddenTextarea(data.ai_prompt || '');
                    const errorMessage = formatImportError(data);
                    enqueueSnackbar(`Error sending data: ${errorMessage}.`, {
                        action: CustomSnackbar,
                        variant: 'error',
                    });
                } else {
                    setImportIssues([]);
                    setAiPrompt('');
                    if (data.dry_run) {
                        enqueueSnackbar(`Validation successful! No data was saved (dry run mode).`, {
                            action: CustomSnackbar,
                            variant: 'success',
                        });
                    } else {
                        enqueueSnackbar(`Flow has been successfully imported.`, {
                            action: CustomSnackbar,
                            variant: 'success',
                        });
                        onClose();
                        if (runAfterImportRef.current && onImportAndRun) {
                            onImportAndRun();
                        } else if (onImportSuccess) {
                            onImportSuccess();
                        }
                    }
                }
            })
            .catch((err) => {
                const errorPayload = err.response?.data;
                const issues = normalizeIssues(errorPayload?.issues);
                // If no issues but there's an error message, create an issue from it
                if (issues.length === 0 && (errorPayload?.error || errorPayload?.message || err.message)) {
                    issues.push({
                        message: errorPayload?.error || errorPayload?.message || err.message,
                        pointer: errorPayload?.trace?.[0]?.file ? `${errorPayload.trace[0].file}:${errorPayload.trace[0].line}` : null
                    });
                }
                setImportIssues(issues);
                setAiPrompt(errorPayload?.ai_prompt || '');
                writePromptToHiddenTextarea(errorPayload?.ai_prompt || '');
                const errorMessage = formatImportError(errorPayload, err.message);
                enqueueSnackbar(`Error sending data: ${errorMessage}`, {
                    action: CustomSnackbar,
                    variant: 'error',
                });
            })
            .finally(() => {
                setIsImporting(false);
            });
    };

    const handleUploadSubmit = () => {
        const codeValue = getValues('code');
        if (!codeValue.trim()) {
            enqueueSnackbar('Please enter JSON data', {
                action: CustomSnackbar,
                variant: 'error',
            });
            return;
        }

        runAfterImportRef.current = false;
        if (dryRun) {
            performImport();
        } else {
            setShowWarningBeforeImport(true);
        }
    };

    const handleImportAndRun = () => {
        const codeValue = getValues('code');
        if (!codeValue.trim()) {
            enqueueSnackbar('Please enter JSON data', {
                action: CustomSnackbar,
                variant: 'error',
            });
            return;
        }

        runAfterImportRef.current = true;
        setShowWarningBeforeImport(true);
    };

    const handleConfirmImport = () => {
        setShowWarningBeforeImport(false);
        performImport();
    };

    return (
        <>
            {/* Warning Dialog - shown before actual import (not dry-run) */}
            <AlertDialog
                visible={showWarningBeforeImport}
                onClose={() => setShowWarningBeforeImport(false)}
                onAgree={handleConfirmImport}
                title="Import Flow - Warning"
                text="This will completely replace the current flow. All methods, transformations, and port configurations will be deleted. If other flows reference this flow, their behavior may become unpredictable."
                noButtonText="Cancel"
                yesButtonText="Import"
            />

            {/* Import Form Dialog */}
            <Dialog
                open={visible}
                onClose={onClose}
                TransitionComponent={Transition}
                sx={{
                    '& .MuiDialog-paper': {
                        width: '960px',
                        maxWidth: '100%',
                        maxHeight: '90vh',
                        margin: '16px',
                    },
                }}
            >
                <Box component="form">
                    <DialogTitle
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            paddingBottom: '8px',
                        }}
                    >
                        <Box component="span" sx={{ color: '#3B465C', fontWeight: 600 }}>
                            Import Flow
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button onClick={onClose} variant="outlined" size="small" disabled={isImporting}>
                                Close
                            </Button>
                            <Button
                                onClick={handleUploadSubmit}
                                variant="contained"
                                size="small"
                                disabled={isImporting}
                                startIcon={isImporting ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                                {isImporting ? 'Importing...' : 'Import'}
                            </Button>
                            {onImportAndRun && (
                                <Button
                                    onClick={handleImportAndRun}
                                    variant="contained"
                                    size="small"
                                    disabled={isImporting || dryRun}
                                    sx={{
                                        backgroundColor: '#2FDE8A',
                                        color: '#3B465C',
                                        '&:hover': { backgroundColor: '#2AC87C' },
                                    }}
                                >
                                    Import & Run
                                </Button>
                            )}
                        </Box>
                    </DialogTitle>

                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            {/* File Upload & Validate Only */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: 2 }}>
                                <input
                                    type="file"
                                    accept=".json,application/json"
                                    onChange={handleFileChange}
                                    disabled={isImporting}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={dryRun}
                                            onChange={(e) => setDryRun(e.target.checked)}
                                            disabled={isImporting}
                                            sx={{
                                                color: '#FF9800',
                                                '&.Mui-checked': {
                                                    color: '#FF9800',
                                                },
                                            }}
                                        />
                                    }
                                    label="Validate only (without saving)"
                                    sx={{
                                        '& .MuiFormControlLabel-label': {
                                            color: '#3B465C',
                                            fontWeight: dryRun ? 600 : 400,
                                        },
                                    }}
                                />
                            </Box>

                            {/* JSON Editor */}
                            <FormControl sx={{ width: '100%', marginBottom: '12px' }}>
                                <Controller
                                    name="code"
                                    control={control}
                                    rules={{
                                        required: 'JSON data is required',
                                    }}
                                    render={({ field }) => (
                                        <>
                                            <div style={{
                                                maxHeight: '50vh',
                                                overflow: 'auto',
                                                borderRadius: '4px',
                                                borderColor: errors.code ? '#f44336': 'rgba(0, 0, 0, 0.23)',
                                                borderWidth: '1px',
                                                borderStyle: 'solid',
                                            }}>
                                                <Editor
                                                    {...field}
                                                    value={code}
                                                    disabled={isImporting}
                                                    onValueChange={(newCode) => {
                                                        setCode(newCode);
                                                        setValue('code', newCode);
                                                    }}
                                                    onFocus={() => setEditorFocused(true)}
                                                    onBlur={() => setEditorFocused(false)}
                                                    highlight={(newCode) => highlight(newCode, languages.js)}
                                                    padding={10}
                                                    tabSize={4}
                                                    style={{
                                                        backgroundColor: editorFocused ? '#E8F0FE' : '#F2F5F8',
                                                        minHeight: '200px',
                                                    }}
                                                    textareaClassName="editor-textarea-no-outline"
                                                />
                                            </div>
                                            {errors.code && (
                                                <FormHelperText error>
                                                    {errors.code.message}
                                                </FormHelperText>
                                            )}
                                        </>
                                    )}
                                />
                            </FormControl>
                            {importIssues.length > 0 && (
                                <Box
                                    sx={{
                                        backgroundColor: '#fdecea',
                                        border: '1px solid #f5c2c0',
                                        borderRadius: '4px',
                                        padding: '12px',
                                        color: '#b3261e',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <strong>Import issues:</strong>
                                        {aiPrompt && config.AI_ENABLED && (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="secondary"
                                                startIcon={<ContentCopyIcon />}
                                                onClick={copyToClipboard}
                                            >
                                                Copy for AI
                                            </Button>
                                        )}
                                    </Box>
                                    <ul style={{ margin: '8px 0 0 16px' }}>
                                        {importIssues.map((issue, index) => (
                                            <li key={`${issue.pointer}-${index}`} style={{ marginBottom: '8px' }}>
                                                <div>
                                                    {issue.message}
                                                    {issue.pointer && (
                                                        <span style={{ color: '#7b1f19' }}> ({issue.pointer})</span>
                                                    )}
                                                    {(issue.line || issue.column) && (
                                                        <span style={{ color: '#7b1f19' }}>
                                                            {' '}
                                                            [line {issue.line ?? '?'}{issue.column ? `, col ${issue.column}` : ''}]
                                                        </span>
                                                    )}
                                                </div>
                                                {issue.valueFragment && (
                                                    <pre
                                                        style={{
                                                            margin: '4px 0 0',
                                                            background: '#fff',
                                                            padding: '8px',
                                                            borderRadius: '4px',
                                                            border: '1px solid #f5c2c0',
                                                            whiteSpace: 'pre-wrap',
                                                        }}
                                                    >
{`${issue.valueFragment}  <-- ${issue.message}`}
                                                    </pre>
                                                )}
                                                {issue.docLink && (
                                                    <div style={{ marginTop: '8px' }}>
                                                        <a
                                                            href={issue.docLink}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                color: '#3498db',
                                                                fontWeight: 600,
                                                                textDecoration: 'none',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '4px'
                                                            }}
                                                        >
                                                            📖 See documentation
                                                        </a>
                                                    </div>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </Box>
                            )}
                        </Box>
                    </DialogContent>
                </Box>
            </Dialog>
            <textarea
                ref={hiddenTextareaRef}
                style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
                tabIndex={-1}
                aria-hidden="true"
            />
            <style>{`
                .editor-textarea-no-outline:focus {
                    outline: none !important;
                }
            `}</style>
        </>
    );
};

export default FlowImportIntoExisting;
